import React, { useEffect, useState } from 'react';
import axios from 'axios';

const statusClass = (status) => ({
  SENT: 'secondary',
  PROCESSED: 'info',
  DELIVERED: 'success',
  DEFERRED: 'warning',
  BOUNCED: 'danger',
  DROPPED: 'danger',
  OPENED: 'primary',
  CLICKED: 'primary',
  UNSUBSCRIBED: 'dark',
  REPORT: 'danger',
}[status] || 'secondary');

const statusDescriptions = [
  {
    value: 'DELIVERED',
    label: 'Entregado',
    english: 'Delivered',
    description: 'El correo fue aceptado por el servidor del destinatario.',
  },
  {
    value: 'PROCESSED',
    label: 'Procesado',
    english: 'Processed',
    description: 'turboSMTP recibio el correo y lo preparo para entrega.',
  },
  {
    value: 'DROPPED',
    label: 'Rechazado',
    english: 'Dropped',
    description: 'turboSMTP descarto el correo y no lo envio.',
  },
  {
    value: 'DEFERRED',
    label: 'Demorado',
    english: 'Deferred',
    description: 'La entrega tuvo un problema temporal y puede reintentarse.',
  },
  {
    value: 'BOUNCED',
    label: 'Rebote',
    english: 'Bounced',
    description: 'La entrega fallo y el correo reboto.',
  },
  {
    value: 'OPENED',
    label: 'Apertura',
    english: 'Opened',
    description: 'El destinatario abrio el correo.',
  },
  {
    value: 'CLICKED',
    label: 'Clic',
    english: 'Clicked',
    description: 'El destinatario hizo clic en un enlace del correo.',
  },
  {
    value: 'REPORT',
    label: 'Reporte de spam',
    english: 'Report',
    description: 'El destinatario marco el correo como spam.',
  },
  {
    value: 'UNSUBSCRIBED',
    label: 'Desuscripcion',
    english: 'Unsubscribed',
    description: 'El destinatario se desuscribio.',
  },
];

const statusInfo = (status) => statusDescriptions.find((item) => item.value === status);
const statusLabel = (status) => {
  const item = statusInfo(status);
  return item ? `${item.label} (${item.english})` : status || '-';
};

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleString();
};

const payloadText = (payload) => {
  if (!payload) return '';
  return JSON.stringify(payload, null, 2);
};

const AdministratorEmailEvents = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState(null);
  const [showStatusHelp, setShowStatusHelp] = useState(false);

  const fetchEvents = (page = 1) => {
    setLoading(true);
    setMessage('');

    return axios.get('/api/administrator/email-events', {
      params: {
        email: email.trim() || undefined,
        status: status || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page,
      },
    })
      .then((response) => {
        const data = response.data.data || {};
        setMessages(data.messages || []);
        setEvents(data.events || []);
        setSummary(response.data.summary || null);
        setStatuses(response.data.filters?.statuses || []);
        setPagination(response.data.pagination || null);
        setMessage(response.data.message || '');
      })
      .catch((error) => {
        setMessages([]);
        setEvents([]);
        setSummary(null);
        setPagination(null);
        setMessage(error.response?.data?.message || 'Ocurrió un error al consultar los eventos.');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const search = (e) => {
    e.preventDefault();
    setExpandedEventId(null);
    fetchEvents(1);
  };

  return (
    <div className="mt-4">
      <h3>Eventos de correos</h3>

      <form className="mb-5" onSubmit={search}>
        <div className="form-row align-items-end">
          <div className="form-group col-md-4">
            <label>Correo</label>
            <input
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@empresa.com"
            />
          </div>
          <div className="form-group col-md-2">
            <div className="d-flex justify-content-between align-items-center">
              <label>Status</label>
              <button type="button" className="btn btn-link btn-sm p-0 mb-2" onClick={() => setShowStatusHelp(true)}>
                Ver tabla
              </button>
            </div>
            <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Todos</option>
              {statuses.map((item) => <option key={item} value={item}>{statusLabel(item)}</option>)}
            </select>
          </div>
          <div className="form-group col-md-2">
            <label>Desde</label>
            <input type="date" className="form-control" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div className="form-group col-md-2">
            <label>Hasta</label>
            <input type="date" className="form-control" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <div className="form-group col-md-2">
            <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
              {loading ? 'Buscando...' : 'Filtrar'}
            </button>
          </div>
        </div>
      </form>

      {message && <div className="alert alert-light">{message}</div>}

      {summary && (
        <div className="row mb-5">
          <div className="col-md-3 mb-3">
            <div className="border rounded p-4 h-100">
              <div className="text-muted">Último status</div>
              <span className={`badge badge-${statusClass(summary.latest_status)} mt-2`}>
                {statusLabel(summary.latest_status)}
              </span>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="border rounded p-4 h-100">
              <div className="text-muted">Envíos del correo filtrado</div>
              <div className="h4 mb-0">{summary.messages_count}</div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="border rounded p-4 h-100">
              <div className="text-muted">Eventos encontrados</div>
              <div className="h4 mb-0">{summary.events_count}</div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div className="border rounded p-4 h-100">
              <div className="text-muted">Último evento</div>
              <div>{formatDate(summary.last_event_at)}</div>
            </div>
          </div>
        </div>
      )}

      {messages.length > 0 && (
        <>
          <h5 className="mb-3">Envíos del correo filtrado</h5>
          <div className="table-responsive mb-5">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Fecha envío</th>
                  <th>Asunto</th>
                  <th>Status</th>
                  <th>MID turboSMTP</th>
                  <th>Message-ID</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((item) => (
                  <tr key={item.id}>
                    <td>{formatDate(item.sent_at)}</td>
                    <td>{item.subject || '-'}</td>
                    <td>
                      <span className={`badge badge-${statusClass(item.latest_status)}`}>{statusLabel(item.latest_status)}</span>
                    </td>
                    <td>{item.turbosmtp_mid || '-'}</td>
                    <td className="text-monospace small">{item.message_id || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h5 className="mb-3">Eventos turboSMTP</h5>
      <div className="table-responsive">
        <table className="table table-hover">
          <thead>
            <tr>
              <th>Fecha evento</th>
              <th>Correo</th>
              <th>Status</th>
              <th>Asunto</th>
              <th>Razón</th>
              <th>Intento</th>
              <th>IP / URL</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && !loading && (
              <tr>
                <td colSpan="8" className="text-muted">Sin eventos para estos filtros.</td>
              </tr>
            )}
            {events.map((item) => (
              <React.Fragment key={item.id}>
                <tr>
                  <td>{formatDate(item.occurred_at)}</td>
                  <td>{item.recipient_email}</td>
                  <td><span className={`badge badge-${statusClass(item.status)}`}>{statusLabel(item.status)}</span></td>
                  <td>{item.subject || '-'}</td>
                  <td>{item.reason ? JSON.stringify(item.reason) : '-'}</td>
                  <td>{item.attempt || '-'}</td>
                  <td>
                    <div>{item.ip || '-'}</div>
                    {item.url && <a href={item.url} target="_blank" rel="noreferrer">{item.url}</a>}
                  </td>
                  <td className="text-right">
                    <button
                      type="button"
                      className="btn btn-sm btn-light"
                      onClick={() => setExpandedEventId(expandedEventId === item.id ? null : item.id)}
                    >
                      Payload
                    </button>
                  </td>
                </tr>
                {expandedEventId === item.id && (
                  <tr>
                    <td colSpan="8">
                      <pre className="bg-light p-3 mb-0" style={{ whiteSpace: 'pre-wrap' }}>{payloadText(item.payload)}</pre>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.total > 0 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="text-muted">
            Mostrando {pagination.from}-{pagination.to} de {pagination.total} eventos
          </span>
          <div>
            <button
              type="button"
              className="btn btn-sm btn-light mr-2"
              disabled={loading || pagination.current_page === 1}
              onClick={() => fetchEvents(pagination.current_page - 1)}
            >
              Anterior
            </button>
            <span className="mr-2">Página {pagination.current_page} de {pagination.last_page}</span>
            <button
              type="button"
              className="btn btn-sm btn-light"
              disabled={loading || pagination.current_page === pagination.last_page}
              onClick={() => fetchEvents(pagination.current_page + 1)}
            >
              Siguiente
            </button>
          </div>
        </div>
      )}

      {showStatusHelp && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}>
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Eventos de correo</h5>
                <button type="button" className="close" aria-label="Cerrar" onClick={() => setShowStatusHelp(false)}>
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Evento</th>
                        <th>Status</th>
                        <th>Significado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusDescriptions.map((item) => (
                        <tr key={item.value}>
                          <td>{item.label}</td>
                          <td><span className={`badge badge-${statusClass(item.value)}`}>{item.english}</span></td>
                          <td>{item.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={() => setShowStatusHelp(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdministratorEmailEvents;
